import {Button, Typography} from '@mui/material';
import BeBornWellImgDesktop from 'presentation/assets/images/img-nascer-bem-desktop@2x.webp';
import BeBornWellImgMobile from 'presentation/assets/images/img-nascer-bem-mobile@2x.webp';

import {WellBornTaggingEnum} from 'main/analytics/gaTagsEnum';
import {ContentState, StepProgressBar} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';

import {
	ButtonStack,
	ContentStack,
	DrawerContent,
	ErrorButtonStack,
	ErrorContainer,
	ImageContainer,
	ProgressBarContainer,
	QuestionStack,
	QuestionTypography,
	StyledDrawer,
	StyledImage,
} from './styles';
import {BeBornWellDrawerProps} from './types';

import {useBeBornWellDrawer} from './hooks/useBeBornWellDrawer';

const {step1, buttons, error, question} =
	translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow.beBornWellDrawer;

function BeBornWellDrawer({
	open,
	setOpen,
	handleNotPregnant,
	handleContinueToBooking,
	onShowBeBornWellCard,
	onShowBeBornWellNotRegister,
}: BeBornWellDrawerProps) {
	const {
		step2Content,
		isError,
		isMobile,
		currentStep,
		isStepOne,
		theme,
		handleClose,
		handlePregnantClick,
		handleNotPregnantClick,
		handleRegisterClick,
		handleNoInterestClick,
		handleRetry,
		declineIsLoading,
		acceptIsLoading,
		disabledButtons,
	} = useBeBornWellDrawer({
		setOpen,
		handleNotPregnant,
		handleContinueToBooking,
		onShowBeBornWellCard,
		onShowBeBornWellNotRegister,
	});

	return (
		<StyledDrawer
			slotProps={{
				paper: {
					sx: {
						borderTopRightRadius: isMobile ? '8px !important' : 0,
						borderTopLeftRadius: isMobile ? '8px !important' : 0,
					},
					'data-tag-id': WellBornTaggingEnum.ENTROU_NO_FLUXO_NASCER_OU_GESTAR_BEM,
				},
			}}
			anchor={isMobile ? 'bottom' : 'right'}
			open={open}
			onClose={handleClose}>
			{isError ? (
				<ErrorContainer $isMobile={isMobile}>
					<ContentState
						variant="info"
						title={error.title}
						description={error.description}
					/>
					<ErrorButtonStack $isMobile={isMobile}>
						<Button variant="outlined" color="primary" fullWidth onClick={handleClose}>
							{error.close}
						</Button>
						<Button variant="contained" color="primary" fullWidth onClick={handleRetry}>
							{error.tryAgain}
						</Button>
					</ErrorButtonStack>
				</ErrorContainer>
			) : (
				<Fragment>
					<ImageContainer>
						<StyledImage src={isMobile ? BeBornWellImgMobile : BeBornWellImgDesktop} />
						<ProgressBarContainer>
							<StepProgressBar
								mt={1}
								px={2}
								py={1}
								currentStep={currentStep}
								totalSteps={2}
								width="100%"
								sx={{backgroundColor: theme.colors.gray['54Percent']}}
							/>
						</ProgressBarContainer>
					</ImageContainer>

					<DrawerContent $isMobile={isMobile}>
						<ContentStack>
							<Typography variant="h6">
								{isStepOne ? step1.title : step2Content.title}
							</Typography>
							<Typography>
								{isStepOne ? step1.description : step2Content.description}
							</Typography>
						</ContentStack>
						<QuestionStack>
							{!isStepOne ? (
								<QuestionTypography $isMobile={isMobile}>
									{question}
								</QuestionTypography>
							) : null}

							<ButtonStack $isMobile={isMobile}>
								<Button
									variant="outlined"
									color="primary"
									fullWidth
									data-tag-id={
										isStepOne
											? WellBornTaggingEnum.CLICK_NAO_ESTOU_GRAVIDA
											: WellBornTaggingEnum.CLICK_NAO_TENHO_INTERESSE
									}
									disabled={disabledButtons}
									onClick={
										isStepOne ? handleNotPregnantClick : handleNoInterestClick
									}
									endIcon={
										declineIsLoading && !isStepOne ? (
											<Loading color="inherit" size={16} />
										) : undefined
									}>
									{isStepOne ? step1.notPregnant : buttons.noInterest}
								</Button>
								<Button
									variant="contained"
									color="primary"
									fullWidth
									disabled={disabledButtons}
									data-tag-id={
										isStepOne
											? WellBornTaggingEnum.CLICK_ESTOU_GRAVIDA
											: WellBornTaggingEnum.CLICK_QUERO_ME_INSCREVER
									}
									onClick={isStepOne ? handlePregnantClick : handleRegisterClick}
									endIcon={
										acceptIsLoading && !isStepOne ? (
											<Loading color="inherit" size={16} />
										) : undefined
									}>
									{isStepOne ? step1.pregnant : buttons.register}
								</Button>
							</ButtonStack>
						</QuestionStack>
					</DrawerContent>
				</Fragment>
			)}
		</StyledDrawer>
	);
}

export {BeBornWellDrawer};
